import { useAuth } from '@/modules/auth/presentation/hooks/use-auth';
import { useRanking } from '@/modules/ranking/presentation/hooks/use-ranking';
import {
  formatPosition,
  toRankingRows,
  type RankingRow,
} from '@/modules/ranking/domain/entities/ranking.entity';
import {
  GABARITO_SUBMISSION_NOTE,
  SendGabaritoAction,
  useGabaritoSubmission,
} from '@/modules/gabarito/presentation/components/SendGabaritoAction';
import { useMyGabarito } from '../hooks/use-my-gabarito';
import { EmptyState, TabSkeleton } from './EmptyState';
import { InviteSection } from './InviteSection';

/**
 * A aba Ranking, no modelo do `enamed-feedback-lp`: a posição da pessoa na
 * lista geral, entre um vizinho de cada lado **borrado** — só ela vê a própria
 * linha em detalhe. A diferença é o critério: aqui é a **nota bruta**
 * (acertos), e não uma nota estimada.
 */
export function RankingTab() {
  const { user } = useAuth();
  const myGabarito = useMyGabarito();
  const ranking = useRanking(myGabarito.data?.submittedAt);
  const submission = useGabaritoSubmission();

  if (myGabarito.isPending || ranking.isPending) {
    return <TabSkeleton label="Carregando sua posição no ranking" />;
  }

  if (!myGabarito.data) {
    return (
      <EmptyState
        title="Ranking"
        note={
          submission === 'open'
            ? 'Envie seu gabarito para entrar no ranking desta prova.'
            : GABARITO_SUBMISSION_NOTE[submission]
        }
      >
        <SendGabaritoAction variant="cta" local="painel" />
      </EmptyState>
    );
  }

  if (ranking.isError) {
    return (
      <EmptyState
        title="Ranking"
        note="Não foi possível carregar sua posição agora. Tente de novo em alguns minutos."
      >
        <button
          type="button"
          className="ghost"
          onClick={() => void ranking.refetch()}
        >
          Tentar de novo
        </button>
      </EmptyState>
    );
  }

  // Gabarito enviado e nenhuma posição: o ranking ainda não foi calculado.
  if (!ranking.data) {
    return (
      <EmptyState
        title="Ranking"
        note="Sua posição aparece aqui assim que o ranking desta prova for calculado."
      />
    );
  }

  const standing = ranking.data;
  const rows = toRankingRows(standing, user?.name || 'Você');

  return (
    <div className="stack-lg">
      <section className="card result-card">
        <p className="figure-label">
          Ranking geral ·{' '}
          <span className="figure-label-accent">nota bruta</span>
        </p>
        <p className="ranking-summary">
          Você está na posição{' '}
          <span className="ranking-highlight">
            {formatPosition(standing.position)}
          </span>{' '}
          de {standing.totalParticipants.toLocaleString('pt-BR')}{' '}
          {standing.totalParticipants === 1 ? 'participante' : 'participantes'}
        </p>

        <ul className="ranking-list">
          {rows.map((row) =>
            row.kind === 'me' ? (
              <MeRow key={row.position} row={row} outOf={standing.outOf} />
            ) : (
              <PeerRow
                key={row.position}
                place={row.position < standing.position ? 'acima' : 'abaixo'}
              />
            ),
          )}
        </ul>

        <p className="result-footnote">
          As posições ao seu redor estão borradas para preservar a privacidade
          dos demais participantes. Apenas você pode ver sua própria posição em
          detalhe.
        </p>
      </section>
      <InviteSection />
    </div>
  );
}

function MeRow({
  row,
  outOf,
}: {
  row: Extract<RankingRow, { kind: 'me' }>;
  outOf: number;
}) {
  return (
    <li
      className="ranking-row is-me"
      aria-label={`Você está aqui: ${formatPosition(row.position)} lugar, ${row.name}, ${row.score} acertos`}
    >
      <span className="ranking-pos">{formatPosition(row.position)}</span>
      <span className="ranking-identity">
        <span className="ranking-name">{row.name}</span>
        <span className="ranking-role">Você está aqui</span>
      </span>
      <span className="ranking-score">
        {row.score}
        <span className="ranking-of">/{outOf}</span>
      </span>
    </li>
  );
}

/**
 * O vizinho, **só como forma**: pontos no lugar de número, nome e nota. Nada
 * dele chega ao DOM, nem escondido — é o que garante a privacidade, e não um
 * `blur` que um inspetor de elementos desfaz.
 */
function PeerRow({ place }: { place: 'acima' | 'abaixo' }) {
  const seed = place === 'acima' ? 1 : 2;
  return (
    <li
      className="ranking-row"
      aria-label={`Participante ${place} de você, com os dados ocultos para preservar a privacidade`}
    >
      <span className="ranking-pos ranking-fill" aria-hidden="true">
        {'•'.repeat(2 + seed)}
      </span>
      <span className="ranking-identity" aria-hidden="true">
        <span className="ranking-name ranking-fill">
          {'•'.repeat(10 + seed)}
        </span>
      </span>
      <span className="ranking-score ranking-fill" aria-hidden="true">
        {'•'.repeat(2 + seed)}
      </span>
    </li>
  );
}
